import { useState, useEffect, useRef } from "react";
import { ChevronDown, Search } from "lucide-react";
import { DIAL_CODES, getDialEntry, parsePhone, type DialEntry } from "@/lib/dialCodes";

interface PhoneInputProps {
  name: string;
  defaultValue?: string;
  country?: string;
  placeholder?: string;
  className?: string;
  onChange?: (value: string) => void;
}

export function PhoneInput({ name, defaultValue = "", country, placeholder, className = "", onChange }: PhoneInputProps) {
  const parsed = parsePhone(defaultValue);

  const initialEntry = (): DialEntry => {
    if (country) return getDialEntry(country) ?? DIAL_CODES.find(d => d.dial === parsed.dial) ?? DIAL_CODES.find(d => d.code === "US")!;
    return DIAL_CODES.find(d => d.dial === parsed.dial && parsed.dial !== "+1") ?? DIAL_CODES.find(d => d.code === "US")!;
  };

  const [selected, setSelected] = useState<DialEntry>(initialEntry);
  const [local, setLocal] = useState(parsed.local);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (country) {
      const entry = getDialEntry(country);
      if (entry) setSelected(entry);
    }
  }, [country]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) searchRef.current.focus();
  }, [open]);

  const combined = local ? `${selected.dial} ${local}` : "";

  useEffect(() => {
    onChange?.(combined);
  }, [combined]);

  const filtered = search
    ? DIAL_CODES.filter(d =>
        d.country.toLowerCase().includes(search.toLowerCase()) ||
        d.dial.includes(search)
      )
    : DIAL_CODES;

  const handleSelect = (entry: DialEntry) => {
    setSelected(entry);
    setOpen(false);
    setSearch("");
  };

  return (
    <div className={`flex rounded-xl border border-slate-200 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 overflow-hidden bg-white ${className}`}>
      <input type="hidden" name={name} value={combined} />

      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setOpen(v => !v)}
          className="flex items-center gap-1.5 px-3 h-full border-r border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors text-sm font-medium text-slate-700 whitespace-nowrap min-w-[88px]"
        >
          <span className="text-base leading-none">{selected.flag}</span>
          <span className="text-slate-600">{selected.dial}</span>
          <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>

        {open && (
          <div className="absolute top-full left-0 z-50 mt-1 w-64 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
            <div className="p-2 border-b border-slate-100">
              <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5">
                <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search country…"
                  className="flex-1 bg-transparent text-sm outline-none text-slate-700 placeholder-slate-400"
                />
              </div>
            </div>
            <div className="max-h-52 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-4">No results</p>
              ) : (
                filtered.map(d => (
                  <button
                    key={d.code}
                    type="button"
                    onClick={() => handleSelect(d)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-slate-50 transition-colors ${
                      d.code === selected.code ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-700"
                    }`}
                  >
                    <span className="text-base leading-none">{d.flag}</span>
                    <span className="flex-1 truncate">{d.country}</span>
                    <span className="text-slate-400 text-xs shrink-0">{d.dial}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <input
        type="tel"
        value={local}
        onChange={e => setLocal(e.target.value.replace(/[^\d\s\-().]/g, ""))}
        placeholder={placeholder ?? selected.example}
        className="flex-1 px-3 py-2.5 text-sm outline-none bg-transparent text-slate-900 placeholder-slate-400 min-w-0"
      />
    </div>
  );
}
